import { CaptchaStatus } from '../models/app';
import { ParsedError, ParsedErrorType } from '../models/error';
import { AppNamespace } from '../state/app';

export enum AppActionType {
    initializeApp = 'initializeApp',
    retrieveOrderFailure = 'retrieveOrderFailure',
    removeAppError = 'removeAppError',
    setCaptchaV3Status = 'setCaptchaV3Status',
    setCaptchaV3Token = 'setCaptchaV3Token',
    setIsCaptchaV3TimeoutReached = 'setIsCaptchaV3TimeoutReached',
    setIsAppRebrandActiveFromInitialState = 'setIsAppRebrandActiveFromInitialState',
    setReloadAfterCheckout = 'setReloadAfterCheckout',
    updatePayinLockStatus = 'updatePayinLockStatus',
    wasExternalPaymentWindowBlocked = 'wasExternalPaymentWindowBlocked',
}

export interface InitializeAppAction {
    type: AppActionType.initializeApp;
    payload: AppNamespace;
}

export interface RetrieveOrderFailureAction {
    type: AppActionType.retrieveOrderFailure;
    payload: ParsedError;
}

export interface RemoveAppErrorAction {
    type: AppActionType.removeAppError;
    payload: ParsedErrorType;
}

export interface SetReloadAfterCheckoutAction {
    type: AppActionType.setReloadAfterCheckout;
    payload: boolean;
}

export interface SetCaptchaV3TokenAction {
    type: AppActionType.setCaptchaV3Token;
    payload: string;
}

export interface SetCaptchaV3StatusAction {
    type: AppActionType.setCaptchaV3Status;
    payload: CaptchaStatus;
}

export interface SetIsCaptchaV3TimeoutReachedAction {
    type: AppActionType.setIsCaptchaV3TimeoutReached;
    payload: boolean;
}

export interface SetIsAppRebrandActiveFromInitialStateAction {
    type: AppActionType.setIsAppRebrandActiveFromInitialState;
    payload: boolean;
}

export interface UpdatePayinLockStatusAction {
    type: AppActionType.updatePayinLockStatus;
    payload: boolean;
}

export interface WasExternalPaymentWindowBlockedAction {
    type: AppActionType.wasExternalPaymentWindowBlocked;
    payload: boolean;
}

export const initializeAppAction = (
    initialData: AppNamespace,
): InitializeAppAction => ({
    type: AppActionType.initializeApp,
    payload: initialData,
});

export const retrieveOrderFailureAction = (
    error: ParsedError,
): RetrieveOrderFailureAction => ({
    type: AppActionType.retrieveOrderFailure,
    payload: error,
});

export const removeAppErrorAction = (
    errorType: ParsedErrorType,
): RemoveAppErrorAction => ({
    type: AppActionType.removeAppError,
    payload: errorType,
});

export const setCaptchaV3TokenAction = (
    token: string,
): SetCaptchaV3TokenAction => ({
    type: AppActionType.setCaptchaV3Token,
    payload: token,
});

export const setCaptchaV3StatusAction = (
    status: CaptchaStatus,
): SetCaptchaV3StatusAction => ({
    type: AppActionType.setCaptchaV3Status,
    payload: status,
});

export const setIsCaptchaV3TimeoutReachedAction = (
    timeoutReached: boolean,
): SetIsCaptchaV3TimeoutReachedAction => ({
    type: AppActionType.setIsCaptchaV3TimeoutReached,
    payload: timeoutReached,
});

export const setIsAppRebrandActiveFromInitialStateAction = (
    isAppRebrandActiveFromInitialState: boolean,
): SetIsAppRebrandActiveFromInitialStateAction => ({
    type: AppActionType.setIsAppRebrandActiveFromInitialState,
    payload: isAppRebrandActiveFromInitialState,
});

export const setReloadAfterCheckoutAction = (
    reloadAfterCheckout: boolean,
): SetReloadAfterCheckoutAction => ({
    type: AppActionType.setReloadAfterCheckout,
    payload: reloadAfterCheckout,
});

export const setWasExternalPaymentWindowBlocked = (
    wasExternalPaymentWindowBlocked: boolean,
): WasExternalPaymentWindowBlockedAction => ({
    type: AppActionType.wasExternalPaymentWindowBlocked,
    payload: wasExternalPaymentWindowBlocked,
});

const updatePayinLockStatusAction = (
    locked: boolean,
): UpdatePayinLockStatusAction => ({
    type: AppActionType.updatePayinLockStatus,
    payload: locked,
});

export const lockPayinAmountAction = () => updatePayinLockStatusAction(true);
export const unlockPayinAmountAction = () => updatePayinLockStatusAction(false);

export type AppAction =
    | InitializeAppAction
    | RetrieveOrderFailureAction
    | RemoveAppErrorAction
    | SetCaptchaV3TokenAction
    | SetCaptchaV3StatusAction
    | SetIsCaptchaV3TimeoutReachedAction
    | SetIsAppRebrandActiveFromInitialStateAction
    | SetReloadAfterCheckoutAction
    | UpdatePayinLockStatusAction
    | WasExternalPaymentWindowBlockedAction;
